import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Layout, Menu, Badge, Avatar, Dropdown, Space, Button, Tooltip, Select, Tag } from 'antd'
import type { MenuProps } from 'antd'
import {
  DashboardOutlined,
  ProjectOutlined,
  SendOutlined,
  MessageOutlined,
  UserOutlined,
  SettingOutlined,
  BarChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SwapOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  SearchOutlined,
  WarningOutlined,
  AppstoreOutlined,
} from '@ant-design/icons'
import { useAppStore, useAuthStore } from '../store'
import { useUnprocessedRepliesCount, useAccounts } from '../hooks'

const { Header, Sider, Content } = Layout

// 生成菜单项配置（根据用户角色）
const getMenuItems = (isAdmin: boolean): MenuProps['items'] => {
  const baseItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表盘',
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: '项目管理',
    },
    {
      key: '/scrape',
      icon: <SearchOutlined />,
      label: '信息抓取',
    },
    {
      key: '/outreach',
      icon: <SendOutlined />,
      label: '触达中心',
    },
    {
      key: '/kanban',
      icon: <AppstoreOutlined />,
      label: '项目看板',
    },
    {
      key: '/replies',
      icon: <MessageOutlined />,
      label: '回复处理',
    },
    {
      key: '/accounts',
      icon: <UserOutlined />,
      label: '账号管理',
    },
  ]

  // 管理员专属菜单
  if (isAdmin) {
    baseItems.push(
      {
        key: '/users',
        icon: <TeamOutlined />,
        label: '用户管理',
      },
      {
        key: '/conflicts',
        icon: <WarningOutlined />,
        label: '冲突管理',
      }
    )
  }

  // 公共菜单
  baseItems.push(
    {
      key: '/statistics',
      icon: <BarChartOutlined />,
      label: '数据统计',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    }
  )

  return baseItems
}

function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])

  const { sidebarCollapsed, toggleSidebar } = useAppStore()
  const {
    user,
    logout,
    impersonatedUser,
    impersonatedUserId,
    bdUsers,
    loadBdUsers,
    setImpersonation
  } = useAuthStore()
  const { data: unprocessedData } = useUnprocessedRepliesCount()
  const { data: accounts, refetch: refetchAccounts } = useAccounts()

  // 管理员登录时加载BD用户列表
  useEffect(() => {
    if (user?.role === 'admin') {
      loadBdUsers()
    }
  }, [user?.role, loadBdUsers])

  // 判断是否为管理员
  const isAdmin = user?.role === 'admin'

  // 获取显示的用户名
  const displayName = impersonatedUser?.name ?? user?.name ?? '用户'
  const displayRole = impersonatedUser ? 'BD (模拟中)' : (user?.role === 'admin' ? '管理员' : 'BD')
  
  // 计算账号健康状态
  const healthyAccounts = accounts?.filter(a => a.health === 'healthy').length ?? 0
  const totalAccounts = accounts?.length ?? 0
  const hasUnhealthyAccounts = healthyAccounts < totalAccounts

  // 获取菜单项
  const menuItems = getMenuItems(isAdmin)

  // 监听路由变化
  useEffect(() => {
    const path = location.pathname
    // 匹配一级路由
    const matchedKey = menuItems
      ?.map((item) => item?.key as string)
      .find((key) => path.startsWith(key))
    if (matchedKey) {
      setSelectedKeys([matchedKey])
    }
  }, [location.pathname, isAdmin])

  // 菜单点击处理
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key)
  }

  // 处理登出
  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // 处理用户菜单点击
  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      handleLogout()
    } else if (key === 'profile') {
      navigate('/settings')
    }
  }

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ]

  // 为回复菜单添加徽章
  const menuItemsWithBadge = menuItems?.map((item) => {
    if (item && 'key' in item && item.key === '/replies') {
      return {
        ...item,
        label: (
          <span>
            回复处理
            {(unprocessedData?.total ?? 0) > 0 && (
              <Badge
                count={unprocessedData?.total}
                size="small"
                style={{ marginLeft: 8 }}
              />
            )}
          </span>
        ),
      }
    }
    return item
  })

  return (
    <Layout className="main-layout">
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={sidebarCollapsed}
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        {/* Logo */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {sidebarCollapsed ? (
            <span style={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>BD</span>
          ) : (
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
              BD Automation
            </span>
          )}
        </div>

        {/* 菜单 */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItemsWithBadge}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      {/* 主内容区 */}
      <Layout className={`site-layout ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* 顶部栏 */}
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          {/* 左侧 */}
          <Space>
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSidebar}
              style={{ fontSize: 16 }}
            />
          </Space>

          {/* 右侧 */}
          <Space size="middle">
            {/* 刷新按钮 */}
            <Tooltip title="刷新数据">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  refetchAccounts()
                  window.location.reload()
                }}
              />
            </Tooltip>

            {/* 账号状态指示 */}
            <Tooltip title={`账号状态：${healthyAccounts}/${totalAccounts} 正常`}>
              <Badge
                status={hasUnhealthyAccounts ? 'warning' : 'success'}
                text={
                  <span style={{ color: hasUnhealthyAccounts ? '#faad14' : '#52c41a' }}>
                    {healthyAccounts}/{totalAccounts}
                  </span>
                }
              />
            </Tooltip>

            {/* 通知 */}
            <Tooltip title="未处理回复">
              <Badge count={unprocessedData?.total ?? 0} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  onClick={() => navigate('/replies')}
                />
              </Badge>
            </Tooltip>

            {/* 管理员模拟用户选择 */}
            {isAdmin && (
              <Space>
                <SwapOutlined style={{ color: '#1890ff' }} />
                <Select
                  placeholder="选择BD查看数据"
                  allowClear
                  style={{ width: 160 }}
                  value={impersonatedUserId}
                  onChange={(userId) => {
                    if (userId) {
                      const selectedUser = bdUsers.find(u => u.id === userId)
                      setImpersonation(userId, selectedUser)
                    } else {
                      setImpersonation(null, null)
                    }
                    // 切换用户后，清除所有缓存的查询数据，重新获取新用户的数据
                    queryClient.invalidateQueries()
                  }}
                  options={bdUsers.map(u => ({
                    value: u.id,
                    label: u.name
                  }))}
                />
                {impersonatedUser && (
                  <Tag
                    color="orange"
                    closable
                    onClose={() => {
                      setImpersonation(null, null)
                      queryClient.invalidateQueries()
                    }}
                    icon={<CloseCircleOutlined />}
                  >
                    查看中: {impersonatedUser.name}
                  </Tag>
                )}
              </Space>
            )}

            {/* 用户菜单 */}
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: isAdmin ? '#722ed1' : '#1890ff' }}
                />
                <span>{displayName}</span>
                <Tag color={isAdmin ? 'purple' : 'blue'} style={{ marginLeft: 4 }}>
                  {displayRole}
                </Tag>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* 内容区 */}
        <Content className="content-wrapper">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
